/**
 * Funciones puras del módulo radio.
 *
 * Sin estado, sin DOM. Cada función recibe entradas y devuelve salidas.
 * Es el sitio donde irían tests unitarios cuando los introduzcamos.
 */

import { AVAILABLE_COVERS } from './data.js';
import type { CoverMeta } from './types.js';

/** Extensiones de archivo de audio reconocidas. */
export const AUDIO_EXTENSION_REGEX = /\.(mp3|wav|ogg|m4a|flac)$/i;

/**
 * Devuelve el nombre legible de una pista quitando la extensión de archivo.
 * `"Hangar Mix.mp3"` → `"Hangar Mix"`.
 */
export function displayName(name: string): string {
  return String(name).replace(AUDIO_EXTENSION_REGEX, '');
}

/**
 * Quita cualquier extensión de archivo (no sólo audio). Usado por
 * `reconcileGhosts()` para comparar nombres de tracks.
 */
export function stripExtension(name: string): string {
  return name.replace(/\.[^.]+$/, '');
}

/** Comprueba si un archivo es de audio por extensión o MIME. */
export function isAudioFile(file: File): boolean {
  return AUDIO_EXTENSION_REGEX.test(file.name) || (file.type || '').startsWith('audio/');
}

/**
 * Hash determinista de una cadena. Usado para generar IDs estables de
 * tracks de usuario (`u_${hashName(name+":"+size)}`).
 *
 * Algoritmo djb2 simplificado. No es criptográfico — sólo distinto-suficiente
 * para identificar tracks en este contexto local.
 */
export function hashName(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0; // forzar 32-bit
  }
  return Math.abs(h).toString(36);
}

/** Genera el ID de track de usuario estándar a partir del nombre y tamaño. */
export function userTrackId(fileName: string, fileSize: number): string {
  return 'u_' + hashName(fileName + ':' + fileSize);
}

/**
 * Formatea segundos en formato `M:SS` (ej: 65 → "1:05"). Devuelve "0:00"
 * si el valor no es finito (NaN, Infinity al inicio antes de cargar).
 */
export function fmtTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Escape básico de HTML para texto que se va a inyectar como innerHTML.
 * Para casos donde NO podemos usar textContent (ej: cuando se construye
 * markup con interpolación). Si se puede, preferir textContent.
 */
export function escapeHTML(s: unknown): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return String(s).replace(/[&<>"']/g, (c) => map[c] ?? c);
}

/**
 * Busca una cover por ID, devolviendo la primera (51sqnplaylist) como
 * fallback si el ID no existe. Garantiza que la UI nunca falla por una
 * cover inexistente referenciada en una playlist guardada.
 */
export function getCover(id: string): CoverMeta {
  return AVAILABLE_COVERS.find((c) => c.id === id) ?? AVAILABLE_COVERS[0]!;
}
